import type { ParseKeys, TFunction } from 'i18next'

import { describeProblem } from '@/lib/problems'
import { refusalMessage } from '@/lib/refusals'
import type { ChecklistIssue } from '@/types/domain'

/* Server facts turned into the reader's sentence.
 *
 * Both of these were duplicated between the detail page and the sections it was
 * split into. A module rather than a component file because neither returns
 * markup — and because a component file may only export components.
 */

/** A server refusal as one line, in the viewer's language.
 *
 * Through describeProblem rather than the problem's own title or detail
 * (AD-22, AD-27): those are written server-side in English for a log, and a
 * Russian user meeting a rejected save is the last person who should be handed
 * a second language. */
export function errorMessage(error: unknown, t: TFunction): string {
  return t(describeProblem(error).detailKey)
}

/** The send's refusal, with the one case the generic mapping cannot word.
 *
 * The name is the offer sheet's; the wording is not, and no longer lives here.
 * A send refused because the issuing entity's branding is incomplete (FR-64) is
 * the same refusal a case document and a client invoice can be answered with —
 * three sheets, one server shape, one sentence — so the reader of that shape sits
 * in `lib/refusals.ts` and this is the quote surface's name for it. Kept as a
 * name rather than collapsed into the call so that the two quote pages holding
 * it keep importing their wording from the module that words everything else
 * they show. */
export function sendErrorMessage(error: unknown, t: TFunction): string {
  return refusalMessage(error, t)
}

/** Why the Send button is refusing, or undefined when it is not.
 *
 * A disabled control that does not say why is a dead end: the reader can see
 * that the offer will not go and has nothing to act on. Both quote surfaces —
 * the detail page and the wizard's footer — ask this, so the wording cannot
 * live at either call site or the two would drift apart on the one question
 * this screen exists to answer.
 *
 * The GATE is not decided here. `sendable` is the server's (FR-3); this only
 * words the refusal, from the same two facts the local fallback reads. Where
 * the server refuses for a reason those facts cannot see, the generic line is
 * what remains true — never a guess at a cause.
 *
 * The two blocking cases are told apart because they need different actions:
 * an empty quote wants positions added, an open checklist wants the positions
 * it already has completed. Sending the reader to "add cargo" on a quote that
 * has four rows would be worse than saying nothing.
 *
 * The strings are the preview sheet's, deliberately. That sheet already
 * explains this same refusal in a banner, and a button whose tooltip disagreed
 * with the banner one press away would leave the reader deciding which to
 * believe. */
export function sendBlockedReason(
  { sendable, hasCargo, blocked }: {
    sendable: boolean
    hasCargo: boolean
    blocked: boolean
  },
  t: TFunction,
): string | undefined {
  if (sendable) return undefined
  if (!hasCargo) return t('quotes.preview.blockedNoCargo')
  if (blocked) return t('quotes.preview.blockedChecklist')
  return t('quotes.preview.blockedGeneric')
}

/* The checklist arrives as codes and a position number; the sentence is built
 * here. The server owns WHICH issues exist and what each blocks — that rule has
 * to come from one place — but the words are this layer's, because it is the
 * layer that knows the reader works in Russian.
 *
 * A position with no number yet takes the unnumbered wording rather than
 * rendering "position undefined" at somebody. */
export function issueText(issue: ChecklistIssue, t: TFunction): string {
  const suffix = issue.position ? '' : '_noPosition'
  /* Widened to the union of its own possible values before the call. The
   * template literal is already exactly that union, but `t` resolves its
   * overloads differently for a union key passed alongside an options object,
   * and picks one that accepts neither. Naming the type keeps every key checked
   * against the catalogue — a misspelling here still fails the build. */
  const key = `quotes.checklist.issue.${issue.code}${suffix}` as ParseKeys
  return t('quotes.checklist.line', {
    issue: t(key, {
      position: issue.position,
    }),
    blocks: t(`quotes.checklist.blocks.${issue.blocks}`),
  })
}
